<template>
  <div class="subs-panel" v-if="user">
    <div class="slide-in__mask animated fadeIn" @click="close"></div>
    <transition name="slideIn">
      <div class="slide-in text-dark" v-if="animate">
        <div class="slide-in__header">
          <div class="slide-in__header__close pointer icon__bg-circle" @click="close">
            <span class="icon-cross2"></span>
          </div>
          <h2>{{ $t('vue2.substitution.add_sub_to_several_lesson') }}</h2>
        </div>
        <div class="slide-in__grid">
          <div class="slide-in__content">
            <div class="slide-in__content__header">
              <div class="slide-in__content__header__left">
                <img :src="user.default_thumb_url" alt=""/>
                <h1>{{ user.name }}</h1>
              </div>
              <div class="flex">
                <AbsenceReason
                  :user_calendar_id="user.user_calendar_id"
                  v-for="absence in absenceReasons"
                  :reason="absence"
                  :key="absence.id"
                  @close="close">
                </AbsenceReason>
              </div>
            </div>
            <div class="slide-in__content__body">
              <label class="d-label">{{ $t('vue2.substitution.choose_period_lessons') }}</label>
              <DoDatePickerRange
                class="marg-1-bottom"
                :dateShortCutsProps="dateShortCutsSorted"
                :allowSameDate="true"
                :startDate="startDate"
                :endDate="endDate"
                @datesChanged="setDates">
              </DoDatePickerRange>
              <SubsUserPanelLessons
                :dates="absentDates"
                :isSubStatusPendingActive="isSubStatusPendingActive"
                :calendarId="user.user_calendar_id"
                :subToAdd="subToAdd"
                @changed="updateSelectedEventIds"
                @updatedArr="updateDbEventsWithSubs">
              </SubsUserPanelLessons>
            </div>
          </div>
          <div class="slide-in__sidebar">
            <SubsUserPanelSubs
              :selectedEvents="selectedEvents"
              @addSub="addSubToEvents"
            ></SubsUserPanelSubs>
          </div>
        </div>
        <transition name="fadeUp">
          <div class="slide-in__footer shadow" v-if="dbEventsWithSubs.length > 0">
            <button @click="cancel">{{ $t('vue2.common.cancel') }}</button>
            <button class="marg-10-left bttn bttn-primary" @click="submit">
              {{ $t('vue2.common.save') }}{{ saveCountSuffix }}
            </button>
          </div>
        </transition>
      </div>
    </transition>
    <DModal v-if="isClosingWithoutSaving" :showClose="false">
      <div slot="insertComponent">
        <div class="d-modal__header">
          <h2>{{ $t('vue2.substitution.do_you_want_to_save') }}?</h2>
          <div class="font-large icon__bg-circle d-modal__header__close" @click="isClosingWithoutSaving = false"><span
            class="icon-cross2"></span></div>
        </div>
        <div class="d-modal__body">
          <div class="d-modal__buttons">
            <button class="marg-10-right" @click="cancel">
              {{ $t('vue2.substitution.no_close_without_saving') }}
            </button>
            <button class="submit-button" @click="submit">{{ $t('vue2.substitution.yes_save_my_changes') }}</button>
          </div>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import AbsenceReason from '../SubsUserAbsenceReason'
import {mapGetters} from 'vuex'
import SubsUserPanelLessons from './SubsUserPanelLessons'
import SubsUserPanelSubs from './SubsUserPanelSubs2'
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import DModal from '../../common/DModal'
import moment from 'moment'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'

export default {
  components: {DoDatePickerRange, AbsenceReason, SubsUserPanelLessons, SubsUserPanelSubs, DModal},
  props: {
    activeDate: {
      default: moment()
    },
    isSubStatusPendingActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dateShortCuts: [
        {
          sortKey: 1,
          title: this.$t('vue2.substitution.selected_date'),
          startDate: moment(this.activeDate).startOf('day'),
          endDate: moment(this.activeDate).endOf('day')
        }
      ],
      startDate: moment(this.activeDate).startOf('day'),
      endDate: moment(this.activeDate).endOf('day'),
      absentDates: [[moment(this.activeDate).startOf('day'), moment(this.activeDate).endOf('day')]],
      isClosingWithoutSaving: false,
      animate: null,
      selectedEventIds: [],
      subToAdd: null,
      dbEventsWithSubs: [] // [{event_id: 29463741, calendar_id: 5, add_substitute_calendar_id: 7},{event_id: 29463742, calendar_id: 5, remove_substitute_calendar_id: 26372}]
    }
  },
  computed: {
    // Antal forskellige lektioner der gemmes ændringer for (submit sender dbEventsWithSubs)
    lessonsToSaveCount () {
      return new Set(this.dbEventsWithSubs.map(eventMap => eventMap.event_id)).size
    },
    saveCountSuffix () {
      const count = this.lessonsToSaveCount
      if (count < 1) {
        return ''
      }
      const words = {
        da: ['lektion', 'lektioner'],
        nb: ['leksjon', 'leksjoner'],
        en: ['lesson', 'lessons']
      }
      const locale = String(this.$store.getters.locale || 'da').slice(0, 2)
      const [singular, plural] = words[locale] || words.da
      return ` (${count} ${count === 1 ? singular : plural})`
    },
    ...mapGetters([
      'allCalendarsInHash',
      'allKlassCalendarsInHash',
      'longAbsentListInHash',
      'activeAbsentUserEventsInHash'
    ]),
    selectedEvents () {
      return this.selectedEventIds.map(eventId => this.activeAbsentUserEventsInHash[eventId])
    },
    allEventsInAHash () {
      return this.$store.state.subs.allEventsInAHash
    },
    user () {
      return this.$store.state.subs.activeAbsentUser
    },
    absenceReasons () {
      return this.user.dayAbsence.map(eventId => this.allEventsInAHash[eventId])
    },
    dateShortCutsSorted () {
      return this.dateShortCuts.slice().sort((a, b) => {
        // Sort by sortKey first
        if (a.sortKey !== b.sortKey) {
          return a.sortKey - b.sortKey
        }
        // If sortKey is equal, sort by title
        return a.title.localeCompare(b.title)
      })
    }
  },
  methods: {
    setDates (startAndEnd) {
      this.startDate = startAndEnd.start
      this.endDate = startAndEnd.end
      this.absentDates = [[this.startDate, this.endDate]]
    },
    setAbsentDates () {
      let isFromTodayAndOnwardsPushed = false
      if (!this.absenceReasons) {
        return
      }

      let globalStartDate = moment(this.activeDate)
      let globalEndDate = moment(this.activeDate)

      // Iterate through all absences
      this.absenceReasons.forEach(absence => {
        let start, end

        if (absence.event_serial_id) {
          const reason = this.longAbsentListInHash[absence.event_serial_id]
          start = moment(reason.from_date)
          end = moment(reason.to_date)
        } else {
          start = moment(absence.start)
          end = moment(absence.end)
        }

        // Push each absence as a shortcut
        this.dateShortCuts.push({
          sortKey: 3,
          title: absence.title || '',
          startDate: start.startOf('day'),
          endDate: end.startOf('day')
        })

        // Update global startDate and endDate if needed
        if (start.isBefore(globalStartDate)) {
          globalStartDate = start
        }
        if (end.isAfter(globalEndDate)) {
          globalEndDate = end
        }
      })

      // If there are multiple absence periods, add a "Whole period" shortcut
      if (this.absenceReasons.length > 1) {
        this.dateShortCuts.push({
          sortKey: 4,
          title: this.$t('vue2.substitution.entire_period'),
          startDate: globalStartDate.startOf('day'),
          endDate: globalEndDate.endOf('day')
        })
      }

      // Add the "From selected date onwards" shortcut only once
      if (moment(this.activeDate).format('YYYY-MM-DD') !== moment(globalStartDate).format('YYYY-MM-DD') && !isFromTodayAndOnwardsPushed) {
        this.dateShortCuts.push({
          sortKey: 2,
          title: this.$t('vue2.substitution.from_selected_date_onwards'),
          startDate: moment(this.activeDate).startOf('day'),
          endDate: globalEndDate.endOf('day')
        })
        isFromTodayAndOnwardsPushed = true
      }
    },
    close () {
      if (this.dbEventsWithSubs.length > 0) {
        this.isClosingWithoutSaving = true
        return
      }
      this.animate = false
      setTimeout(_ => {
        this.$store.commit('setActiveAbsentUser', null)
      }, 250)
    },
    updateSelectedEventIds (arr) {
      this.selectedEventIds = arr
    },
    addSubToEvents (user) {
      this.subToAdd = user
    },
    updateDbEventsWithSubs (arr) {
      this.dbEventsWithSubs = arr
      this.subToAdd = null
    },
    cancel () {
      this.isClosingWithoutSaving = false
      this.dbEventsWithSubs = []
      this.close()
    },
    submit () {
      const updates = {}
      this.dbEventsWithSubs.forEach(eventMap => {
        const eventId = eventMap.event_id
        const event = this.activeAbsentUserEventsInHash[eventId]
        const schoolId = event && event.school_id ? event.school_id : null
        if (!updates[schoolId]) {
          updates[schoolId] = [eventMap]
        } else {
          updates[schoolId].push(eventMap)
        }
      })
      const successTxt = this.$t('vue2.substitution.lessons_updated')
      const errorTxt = this.$t('vue2.common.something_went_wrong')
      Object.keys(updates).forEach(schoolId => {
        docendo.updateEvents(schoolId, updates[schoolId], success => {
          eventbus.toast = {text: successTxt, success: true}
        }, failure => {
          eventbus.toast = {text: errorTxt, success: false}
        })
      })
      this.$emit('readEvents')
      this.dbEventsWithSubs = []
      this.close()
    }
  },
  beforeMount () {
    this.setAbsentDates()
  },
  mounted () {
    this.animate = true
  },
  beforeDestroy () {
    this.animate = false
  }
}
</script>

<style scoped>
@import "../../../../src/assets/css/variables.css";

.slide-in__mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 9990;
  background: rgb(34 34 34 / 35%);
}

.slide-in {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 90vw;
  background: #edf0f5;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, .21);
  border: 1px solid #e6e8eb;
  z-index: 9991;
}

.slide-in__header {
  border-bottom: 1px solid #e6e8eb;
  position: relative;
  background: #ffffff;
  display: flex;
  align-items: center;
  padding: 1rem 0;
}

.slide-in__footer {
  border-top: 1px solid #e6e8eb;
  position: relative;
  background: #ffffff;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 2rem;
}

.slide-in__header__close {
  font-size: 20px;
  margin-left: 0.5rem;
  margin-right: 1rem;
}

.slide-in__grid {
  display: flex;
  min-height: calc(100vh - 110px); /* minus footer and header */
  max-height: calc(100vh - 110px);
}

.slide-in__content {
  flex: 1;
}

.slide-in__content__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  padding: 2rem;
}

.slide-in__content__header__left {
  display: flex;
  align-items: center;
}

.slide-in__content__header__left img {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  margin-right: 2rem;
}

.slide-in__content__header .flex > div {
  margin-left: 1rem;
}

.slide-in__content__body {
  margin: 1rem 2rem;
}

.slide-in__content__body .d-label {
  margin-bottom: 0.5rem;
}

.slide-in__sidebar {
  flex: 1;
  max-width: 350px;
  min-width: 350px;
  border-left: 1px solid var(--border-color);
  max-height: calc(100vh - 55px);
  overflow-y: auto;
}


.slideIn-enter-active, .slideIn-leave-active {
  transition: transform 0.5s;
  transform: translateX(0);
}

.slideIn-enter, .slideIn-leave-to /* .fade-leave-active below version 2.1.8 */
{
  transform: translateX(100%);
}

.fadeUp-enter-active, .fadeUp-leave-active {
  transition: transform 0.5s;
  transform: translateY(0);
}

.fadeUp-enter, .fadeUp-leave-to {
  transform: translateY(100%);
}

</style>



// WEBPACK FOOTER //
// SubsUserPanel.vue?34a8c17c